import type { ReactNode } from "react";

import { ColocaLogo } from "@/shared/components/ColocaLogo";
import useTranslations from "@/shared/lang/useTranslations";
import { useTheme } from "@/shared/theme/useTheme";

import { LangComponent } from "./LangComponent";
import { LangDropdown } from "./LangDropdown";
import { ThemeSwitch } from "./ThemeSwitch";

interface AuthShellProps {
  children: ReactNode;
  /** Side where the brand panel (green LiquidEther animation) renders on xl+. Defaults to "left" (login). */
  panelSide?: "left" | "right";
}

export const AuthShell = ({ children, panelSide = "left" }: AuthShellProps) => {
  const { theme, toggleTheme } = useTheme();
  const { t, activeLanguage, setLanguage } = useTranslations("login");

  return (
    <div className="flex w-full h-screen overflow-hidden">
      <div
        className={panelSide === "right" ? "hidden xl:block xl:w-1/2 order-2" : "hidden xl:block xl:w-1/2"}
      >
        <LangComponent t={t} />
      </div>
      <div className="w-full xl:w-1/2 h-screen overflow-y-auto">
        <div className="flex min-h-full w-full flex-col">
          <div className="flex justify-end p-5 gap-2 animate-fade-down">
            <LangDropdown
              defaultSelected={activeLanguage}
              items={[
                { label: "EN", value: "en" },
                { label: "ES", value: "es" },
                { label: "PT", value: "pt" },
              ]}
              onChange={(value) => setLanguage(value)}
            />
            <ThemeSwitch theme={theme} toggleTheme={toggleTheme} />
          </div>
          <div className="w-full flex-1 flex flex-col justify-center items-center gap-8 px-5 py-8">
            <div className="md:max-w-48 max-w-40 animate-fade-down">
              <ColocaLogo variant={theme === "dark" ? "white" : "black"} />
            </div>
            {children}
          </div>
          <div className="flex flex-col items-center w-full mb-5 text-zinc-700 text-sm gap-2 animate-fade-up dark:text-zinc-200 px-5">
            <a
              href="https://colocapayments.com/terminos/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold cursor-pointer relative inline-block group transition-all duration-300 hover:text-green-500 py-1 dark:hover:text-green-300"
            >
              {t("terms_and_conditions")}
              <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-green-400 dark:bg-green-300 rounded-full transition-all duration-300 group-hover:w-full"></span>
            </a>
            <span className="max-w-lg text-center">
              Coloca Payments - V1.0.0 © {new Date().getFullYear()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
